import cls from 'classnames';
import dynamic from 'next/dynamic';
import { useCallback, useMemo } from 'react';

import styles from './image.module.scss';
import { uploadImageIcon } from '../../assets';
import { defaultAvatar } from '../../assets/Home';
import deleteIcon from '../../assets/Edit/delete.svg';
import editIcon from '../../assets/Edit/edit.svg';
import uploadIcon from '../../assets/Edit/uploadImage.png';
import { usePresetEditState } from '../../hooks/usePresetEditState';
import { setShowEditImage, updateImgInfo, useAppDispatch, useAppSelector } from '../../state';
import { trackWithNpc } from '../Home/NpcTabs';
import { LocalImage } from '../Image';

const ImageEditModal = dynamic(() =>
  import('./imageModal').then(({ ImageEditModal }) => ImageEditModal)
);

const PAGE = 'create_npc_advanced_page';

export interface ImageEditEntryProps {
  text: {
    title: string;
    uploadTab: string;
    mineTab: string;
    uploadTitle: string;
    uploadDesc: string;
    selectImage: string;
    imageUploading: string;
    empty: string;
    reselectImage: string;
    finish: string;
    uploadFailed: string;
    retry: string;
    imageCorpTitle: string;
    imageAvatarCorpTitle: string;
    imageBgCorpTitle: string;
  };
  cdnPrefix: string;
  /**
   * quickcreate-async B 方案 (2026-05-25): 是否正在 polling chat-agent 异步任务.
   * caller (apps/xingye /create/edit) 传 useQuickCreatePolling().status !== 'done'.
   * 为 true 且无 base_img_url 时显示 defaultAvatar 占位 + "图片生成中..." 小标
   * 替代 uploadImageIcon (避免给用户"未上传"的误导).
   */
  isGenerating?: boolean;
  /** 占位文案 — 跟应用 i18n 对齐 (xingye 中文 "图片生成中..." / talkie 英文 "Generating image...") */
  generatingText?: string;
}

function ImageValue(
  props: ImageEditEntryProps & {
    onEdit: () => void;
    onDelete: () => void;
  }
) {
  const { onEdit, onDelete } = props;
  const { face_img_url } = useAppSelector((state) => state.edit.imgInfo);

  return (
    <div className={styles.imgValue}>
      <img src={face_img_url} />

      <div className={styles.btns}>
        <div className={styles.btn} onClick={onEdit}>
          <LocalImage style={{ borderRadius: 0 }} src={editIcon} />
        </div>
        <div className={styles.btn}>
          <LocalImage src={deleteIcon} onClick={onDelete} />
        </div>
      </div>
    </div>
  );
}

export function ImageEditEntry(props: ImageEditEntryProps) {
  const {
    imgInfo: { base_img_url, face_img_url },
    npcId,
    statusInfo,
    showEditImage,
  } = useAppSelector((state) => state.edit);
  const hasValue = useMemo(() => !!base_img_url && !!face_img_url, [base_img_url, face_img_url]);
  /** quickcreate-async B 方案: 正在生成 + 无图 → 走 defaultAvatar 占位 */
  const isGeneratingPlaceholder = useMemo(
    () => !!props.isGenerating && !hasValue,
    [props.isGenerating, hasValue]
  );

  const onClick = useCallback(() => {
    if (!hasValue) {
      trackWithNpc(
        { npc_id: npcId, status_info: statusInfo },
        'image_edit_click',
        PAGE,
        {
          image_edit_click_type: 'upload',
        },
        'collect'
      );
      dispatch(setShowEditImage(true));
    }
  }, [hasValue]);

  const dispatch = useAppDispatch();
  const onEdit = useCallback(() => {
    trackWithNpc(
      { npc_id: npcId, status_info: statusInfo },
      'image_edit_click',
      PAGE,
      {
        image_edit_click_type: 'edit',
      },
      'collect'
    );
    dispatch(setShowEditImage(true));
  }, []);
  const onDelete = useCallback(() => {
    trackWithNpc(
      { npc_id: npcId, status_info: statusInfo },
      'image_edit_click',
      PAGE,
      {
        image_edit_click_type: 'delete',
      },
      'collect'
    );
    dispatch(updateImgInfo(null));
  }, [dispatch]);

  usePresetEditState();

  return (
    <>
      <div
        className={cls(styles.wrapper, {
          [styles.hasValue]: hasValue,
          [styles.isGenerating]: isGeneratingPlaceholder,
        })}
        onClick={isGeneratingPlaceholder ? undefined : onClick}
      >
        {hasValue ? (
          <ImageValue {...props} onDelete={onDelete} onEdit={onEdit} />
        ) : isGeneratingPlaceholder ? (
          <div className={styles.generatingWrapper}>
            <LocalImage className={styles.generatingAvatar} src={defaultAvatar} />
            <span className={styles.generatingTag}>
              {props.generatingText || '图片生成中...'}
            </span>
          </div>
        ) : (
          <LocalImage className={styles.uploadIcon} src={uploadImageIcon} />
        )}
      </div>
      <ImageEditModal
        {...props}
        show={showEditImage}
        setShow={(v) => dispatch(setShowEditImage(v))}
      />
    </>
  );
}
